import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Switch } from "@headlessui/react";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

export default function ManageTarget() {
  const [targets, setTargets] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [newValue, setNewValue] = useState("");
  const [showMonthlyCap, setShowMonthlyCap] = useState(false);
  const [showDailyCap, setShowDailyCap] = useState(false);
  const [showWeeklyCap, setShowWeeklyCap] = useState(false);
  const [showMaxConcurrency, setShowMaxConcurrency] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || "https://didrouteapi.com/api";

  useEffect(() => {
    fetchBuyerTargets();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchBuyerTargets = async () => {
    try {
      const token = localStorage.getItem("buyerToken");

      if (!token) {
        setError('No authentication token found. Please login again.');
        setTargets([]);
        return;
      }

      const response = await fetch(`${API_URL}/buyers/dashboard/targets`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 403) {
        setError('Not authorized as buyer. Please login with a buyer account.');
        setTargets([]);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch targets');
      }

      const targetsData = data.data || [];
      
      if (Array.isArray(targetsData)) {
        const validTargets = targetsData.filter(target => 
          target && 
          typeof target === 'object' && 
          target.id && 
          target.campaign_name
        );
        setTargets(validTargets);
        setError(null);
      } else {
        setTargets([]);
        setError('Invalid data format received');
      }
    } catch (error) {
      setTargets([]);
      setError(error.message);
    }
  };

  const handleStatusToggle = async (id) => {
    try {
      const token = localStorage.getItem('buyerToken');
      const target = targets.find(t => t.id === id);
      
      if (!target) return;

      const response = await fetch(`${API_URL}/buyers/dashboard/targets/${id}/toggle-status`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        await fetchBuyerTargets();
      } else {
        setError(data.error || 'Failed to toggle target status');
      }
    } catch (error) {
      setError('Error toggling target status');
    }
  };

  const handleEdit = (target) => {
    setEditingId(target.id);
    setEditForm({
      target_name: target.target_name,
      target_number: target.target_number,
      max_concurrency: target.max_concurrency || 0,
      monthly_cap: target.monthly_cap || 0,
      daily_cap: target.daily_cap || 0,
      weekly_cap: target.weekly_cap || 0,
      connection_timeout: target.connection_timeout || 0,
      cc_limit_flag: target.cc_limit_flag || 'No',
      monthly_cap_flag: target.monthly_cap_flag || 'No',
      daily_cap_flag: target.daily_cap_flag || 'No',
      weekly_cap_flag: target.weekly_cap_flag || 'No'
    });
    
    setShowMaxConcurrency(target.cc_limit_flag?.toLowerCase() === "yes");
    setShowMonthlyCap(target.monthly_cap_flag?.toLowerCase() === "yes");
    setShowDailyCap(target.daily_cap_flag?.toLowerCase() === "yes");
    setShowWeeklyCap(target.weekly_cap_flag?.toLowerCase() === "yes");
    
    setIsEditModalOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    const numericFields = [
      'target_number',
      'max_concurrency',
      'monthly_cap',
      'daily_cap',
      'weekly_cap',
      'connection_timeout'
    ];

    if (numericFields.includes(name)) {
      const numericValue = value.replace(/[^\d]/g, '').trim();
      setEditForm(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        [name]: value.trim()
      }));
    }
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('buyerToken');
      
      const payload = {
        ...editForm,
        cc_limit_flag: showMaxConcurrency ? "Yes" : "No",
        monthly_cap_flag: showMonthlyCap ? "Yes" : "No",
        daily_cap_flag: showDailyCap ? "Yes" : "No",
        weekly_cap_flag: showWeeklyCap ? "Yes" : "No"
      };

      const response = await fetch(`${API_URL}/buyers/dashboard/targets/${editingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchBuyerTargets();
        setIsEditModalOpen(false);
        setEditingId(null);
        setEditForm({});
        setError(null);
      } else {
        setError(data.error || 'Failed to update target');
      }
    } catch (error) {
      setError('Error updating target');
    }
  };

  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setEditingId(null);
    setEditForm({});
  };

  const filteredTargets = targets.filter((target) =>
    Object.values(target).some((value) =>
      value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <div className="bg-white p-2 px-6 py-10 rounded-lg shadow-lg">
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      <div className="sm:flex sm:items-center bg-white">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Manage Targets
          </h1>
        </div>

        <div className="mt-4 sm:mt-0 sm:ml-16">
          <input
            type="text"
            placeholder="Search targets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full rounded-md border border-gray-700 placeholder:text-gray-900 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2"
          />
        </div>
      </div>

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 custom-scrollbar">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full divide-y divide-gray-700">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-xs font-medium text-black sm:pl-0"
                  >
                    Campaign Name
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-xs font-medium text-black"
                  >
                    Target Name
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-xs font-medium text-black"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-xs font-medium text-black"
                  >
                    Number
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-xs font-medium text-black"
                  >
                    CC
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-xs font-medium text-black"
                  >
                    Today
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-xs font-medium text-black"
                  >
                    Weekly
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-xs font-medium text-black"
                  >
                    Monthly
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-xs font-medium text-black"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredTargets.map((target) => (
                  <tr key={target.id}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900 sm:pl-0">
                      {target.campaign_name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                      {target.target_name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                      {target.type || "N/A"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                      {target.target_number}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                      {target.max_concurrency}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                      {target.daily_cap}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                      {target.weekly_cap}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                      {target.monthly_cap}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                      <div className="flex items-center space-x-3">
                        <Switch
                          checked={target.status === 'active'}
                          onChange={() => handleStatusToggle(target.id)}
                          className={`${
                            target.status === 'active' ? "bg-green-600" : "bg-gray-600"
                          } relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none`}
                        >
                          <span className="sr-only">Toggle status</span>
                          <span
                            className={`${
                              target.status === 'active' ? "translate-x-4" : "translate-x-1"
                            } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                        <button
                          onClick={() => handleEdit(target)}
                          className="text-indigo-400 hover:text-indigo-300"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Transition.Root show={isEditModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative justify-center z-50 overflow-y-auto"
          onClose={setIsEditModalOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900 bg-opacity-80 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                  <div>
                    <div className="mt-1 text-center">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-black mb-4"
                      >
                        Edit Target
                      </Dialog.Title>

                      <div className="mt-2 space-y-2">
                        <div className="text-left">
                          <label
                            htmlFor="target_name"
                            className="block text-sm font-medium text-gray-900"
                          >
                            Target Name *
                          </label>
                          <input
                            type="text"
                            name="target_name"
                            id="target_name"
                            value={editForm.target_name || ""}
                            // onChange={handleEditChange}
                            className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="text-left">
                          <label
                            htmlFor="target_number"
                            className="block text-sm font-medium text-gray-900"
                          >
                            Target Number *
                          </label>
                          <input
                            type="number"
                            name="target_number"
                            id="target_number"
                            value={editForm.target_number || ""}
                            // onChange={handleEditChange}
                            className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                            min="1"
                          />
                        </div>

                        <div className="text-left">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-900">
                              Max Concurrency
                            </label>
                            <input
                              type="checkbox"
                              checked={showMaxConcurrency}
                              onChange={() =>
                                setShowMaxConcurrency(!showMaxConcurrency)
                              }
                              className="form-checkbox h-4 w-4 text-indigo-600"
                            />
                          </div>
                          {showMaxConcurrency && (
                            <input
                              type="number"
                              name="max_concurrency"
                              value={editForm.max_concurrency || ""}
                              onChange={handleEditChange}
                              className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              min="1"
                            />
                          )}
                        </div>

                        <div className="text-left">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-900">
                              Monthly Cap
                            </label>
                            <input
                              type="checkbox"
                              checked={showMonthlyCap}
                              onChange={() =>
                                setShowMonthlyCap(!showMonthlyCap)
                              }
                              className="form-checkbox h-4 w-4 text-indigo-600"
                            />
                          </div>
                          {showMonthlyCap && (
                            <input
                              type="number"
                              name="monthly_cap"
                              value={editForm.monthly_cap || ""}
                              onChange={handleEditChange}
                              className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              min="0"
                            />
                          )}
                        </div>

                        <div className="text-left">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-900">
                              Daily Cap
                            </label>
                            <input
                              type="checkbox"
                              checked={showDailyCap}
                              onChange={() => setShowDailyCap(!showDailyCap)}
                              className="form-checkbox h-4 w-4 text-indigo-600"
                            />
                          </div>
                          {showDailyCap && (
                            <input
                              type="number"
                              name="daily_cap"
                              value={editForm.daily_cap || ""}
                              onChange={handleEditChange}
                              className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              min="0"
                            />
                          )}
                        </div>

                        <div className="text-left">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-900">
                              Weekly Cap
                            </label>
                            <input
                              type="checkbox"
                              checked={showWeeklyCap}
                              onChange={() => setShowWeeklyCap(!showWeeklyCap)}
                              className="form-checkbox h-4 w-4 text-indigo-600"
                            />
                          </div>
                          {showWeeklyCap && (
                            <input
                              type="number"
                              name="weekly_cap"
                              value={editForm.weekly_cap || ""}
                              onChange={handleEditChange}
                              className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              min="0"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                      onClick={handleSaveEdit}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-600 sm:col-start-1 sm:mt-0"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}